import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Lightbulb, Settings, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section } from '../components/shared/Section';
import { GlassCard } from '../components/shared/GlassCard';
import { Particles } from '../components/shared/Particles';
import { SEO } from '../components/shared/SEO';
import { CONTACT_INFO } from '../lib/constants';

// ─── JSON-LD ──────────────────────────────────────────────────────────────────
const ABOUT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://hydroxstudios.com/about',
  url: 'https://hydroxstudios.com/about',
  name: 'About Hydrox Studios',
  description:
    'Hydrox Studios is a professional music video and audio post-production studio based in Guwahati, Assam, serving artists and brands across Northeast India.',
  mainEntity: {
    '@type': 'LocalBusiness',
    '@id': 'https://hydroxstudios.com/#organization',
    name: 'Hydrox Studios',
    foundingLocation: {
      '@type': 'Place',
      name: 'Guwahati, Assam, India',
    },
    description:
      'Boutique post-production studio specialising in music video production, audio mixing & mastering, brand films, and cinematic post-production.',
    knowsAbout: [
      'Music Video Production',
      'Audio Mixing',
      'Audio Mastering',
      'Brand Films',
      'Video Post-Production',
      'Colour Grading',
    ],
  },
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hydroxstudios.com/' },
    { '@type': 'ListItem', position: 2, name: 'About', item: 'https://hydroxstudios.com/about' },
  ],
};

// ─── Breadcrumb UI ────────────────────────────────────────────────────────────
const Breadcrumb = () => (
  <nav aria-label="Breadcrumb" className="absolute top-24 left-6 z-20 hidden sm:block">
    <ol className="flex items-center gap-2 text-xs font-manrope text-white/40">
      <li>
        <Link to="/" className="hover:text-white/70 transition-colors">
          Home
        </Link>
      </li>
      <li aria-hidden="true">/</li>
      <li>
        <span className="text-primary" aria-current="page">
          About
        </span>
      </li>
    </ol>
  </nav>
);

// ─── Page Hero ────────────────────────────────────────────────────────────────
const PageHero = () => (
  <section
    className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20"
    aria-labelledby="about-page-heading"
  >
    <Breadcrumb />
    <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" aria-hidden="true" />
    <Particles count={15} />

    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-sm uppercase tracking-[0.3em] text-primary font-manrope mb-6"
      >
        About Us · Guwahati, Assam
      </motion.p>

      <motion.h1
        id="about-page-heading"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6"
      >
        About Hydrox Studios – Creative Production Studio in Guwahati
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="font-manrope text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8"
      >
        Where technical discipline meets creative vision — built for artists and brands
        across Northeast India.
      </motion.p>

      {/* Quick-action link anchors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="flex items-center justify-center gap-6 flex-wrap"
      >
        <a
          href="#story"
          className="text-sm font-manrope text-white/50 hover:text-primary transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Our Story
        </a>
        <span className="text-white/20" aria-hidden="true">·</span>
        <a
          href="#philosophy"
          className="text-sm font-manrope text-white/50 hover:text-primary transition-colors"
        >
          Our Philosophy
        </a>
        <span className="text-white/20" aria-hidden="true">·</span>
        <Link
          to="/services"
          className="text-sm font-manrope text-white/50 hover:text-primary transition-colors"
        >
          Our Services
        </Link>
      </motion.div>
    </div>
  </section>
);

// ─── Story Section ────────────────────────────────────────────────────────────
const StorySection = () => (
  <Section id="story" aria-labelledby="story-heading">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-manrope mb-4">
          Our Story
        </p>
        <h2
          id="story-heading"
          className="font-syne text-3xl md:text-4xl font-bold text-white leading-tight mb-6"
        >
          Built for Creators Who Demand Excellence
        </h2>
        <div className="space-y-4 text-muted-foreground font-manrope text-base leading-relaxed">
          <p>
            HYDROX STUDIOS is a Guwahati-based music video and audio production studio
            founded on a simple principle: every creator deserves access to premium
            post-production quality, regardless of scale.
          </p>
          <p>
            We combine technical precision with cinematic sensibility, applying the same
            structured workflows and attention to detail to every project — whether it's
            an independent artist's debut single or a brand's flagship campaign.
          </p>
          <p>
            Our approach is deliberate. We don't chase trends or cut corners. Instead, we
            focus on delivering work that stands the test of time, built on clear
            communication, transparent processes, and unwavering quality standards.
          </p>
        </div>

        {/* Internal link to services — boosts crawl depth */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/services">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 font-manrope"
              aria-label="Explore Hydrox Studios services"
            >
              Explore Our Services
              <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </Button>
          </Link>
          <Link to="/music-video-production-guwahati">
            <Button
              variant="ghost"
              className="text-primary hover:text-primary/80 font-manrope px-0 underline-offset-4 hover:underline"
              aria-label="Learn about music video production in Guwahati"
            >
              Music Videos in Guwahati →
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative">
        <GlassCard className="p-8" hover={false}>
          <img
            src="https://images.unsplash.com/photo-1550524756-d07d631274cc?auto=format&fit=crop&w=800&q=80"
            alt="Professional audio mixing console at Hydrox Studios in Guwahati"
            width={800}
            height={320}
            loading="lazy"
            decoding="async"
            className="w-full h-80 object-cover rounded-lg"
          />
        </GlassCard>
        <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-primary/30 rounded-lg -z-10" aria-hidden="true" />
      </div>
    </div>
  </Section>
);

// ─── Philosophy Section ───────────────────────────────────────────────────────
const PHILOSOPHIES = [
  {
    icon: Target,
    title: 'Technical Precision',
    description:
      'Every detail matters. We apply rigorous technical standards to ensure your content meets and exceeds industry benchmarks.',
  },
  {
    icon: Eye,
    title: 'Cinematic Vision',
    description:
      'We approach every project with a cinematic sensibility — understanding that great content tells a story and evokes emotion.',
  },
  {
    icon: Settings,
    title: 'Structured Process',
    description:
      'Clear timelines, defined deliverables, and consistent communication. Our workflow is designed for reliability and excellence.',
  },
  {
    icon: Lightbulb,
    title: 'Creative Partnership',
    description:
      'We collaborate closely with every client, ensuring your vision is not just executed but elevated through our expertise.',
  },
];

const PhilosophySection = () => (
  <Section id="philosophy" className="bg-black/30" aria-labelledby="philosophy-heading">
    <div className="text-center mb-16">
      <p className="text-sm uppercase tracking-widest text-primary font-manrope mb-4">
        Our Philosophy
      </p>
      <h2
        id="philosophy-heading"
        className="font-syne text-3xl md:text-4xl font-bold text-white"
      >
        How We Approach Every Project
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" role="list">
      {PHILOSOPHIES.map((item, index) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.title}
            role="listitem"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <GlassCard className="p-8 h-full">
              <div
                className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6"
                aria-hidden="true"
              >
                <Icon size={24} className="text-primary" />
              </div>
              <h3 className="font-syne text-xl font-semibold text-white mb-3">
                {item.title}
              </h3>
              <p className="text-muted-foreground font-manrope text-sm leading-relaxed">
                {item.description}
              </p>
            </GlassCard>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

// ─── Approach Section ─────────────────────────────────────────────────────────
const ApproachSection = () => (
  <Section aria-labelledby="approach-heading">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div className="relative order-2 lg:order-1">
        <GlassCard className="p-8" hover={false}>
          <img
            src="https://images.unsplash.com/photo-1726994133956-e994d84d3d93?auto=format&fit=crop&w=800&q=80"
            alt="Cinema camera setup used for music video production in Guwahati by Hydrox Studios"
            width={800}
            height={320}
            loading="lazy"
            decoding="async"
            className="w-full h-80 object-cover rounded-lg"
          />
        </GlassCard>
        <div className="absolute -top-4 -left-4 w-24 h-24 border border-primary/30 rounded-lg -z-10" aria-hidden="true" />
      </div>

      <div className="order-1 lg:order-2">
        <p className="text-sm uppercase tracking-widest text-primary font-manrope mb-4">
          Creative Direction
        </p>
        <h2
          id="approach-heading"
          className="font-syne text-3xl md:text-4xl font-bold text-white leading-tight mb-6"
        >
          Your Vision, Elevated
        </h2>
        <div className="space-y-4 text-muted-foreground font-manrope text-base leading-relaxed">
          <p>
            We believe great post-production isn't about imposing a style — it's about
            understanding your vision and bringing it to life with technical excellence.
          </p>
          <p>
            Every project begins with listening. We take the time to understand your goals,
            your audience, and your creative direction. Only then do we apply our expertise
            to enhance and refine your work.
          </p>
          <p>
            The result? Content that feels authentically yours, but polished to a level
            that competes with the best in the industry.
          </p>
        </div>
      </div>
    </div>
  </Section>
);

// ─── CTA Section ──────────────────────────────────────────────────────────────
const CTASection = () => {
  const whatsappNumber = CONTACT_INFO.whatsapp?.replace('+', '') ?? '';
  const whatsappHref = `https://wa.me/${whatsappNumber}?text=Hi%2C%20I'd%20like%20to%20learn%20more%20about%20HYDROX%20STUDIOS.`;

  return (
    <Section aria-labelledby="about-cta-heading">
      <div className="relative overflow-hidden rounded-2xl glass-heavy p-12 md:p-16 text-center">
        <div className="hero-glow -top-32 -left-32" aria-hidden="true" />
        <div className="hero-glow -bottom-32 -right-32" aria-hidden="true" />

        <div className="relative z-10">
          <h2
            id="about-cta-heading"
            className="font-syne text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Ready to Work Together?
          </h2>
          <p className="text-muted-foreground font-manrope text-base max-w-xl mx-auto mb-8">
            Let's discuss your project and explore how we can help bring your vision to life.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="about-page-cta-primary"
                size="lg"
                className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope px-8"
                aria-label="Contact Hydrox Studios to start your project"
              >
                Get a Quote
                <ArrowRight size={18} className="ml-2" aria-hidden="true" />
              </Button>
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with Hydrox Studios on WhatsApp"
            >
              <Button
                data-testid="about-page-cta-secondary"
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 font-manrope px-8"
              >
                WhatsApp Direct
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function About() {
  return (
    <div data-testid="about-page">
      {/* ── SEO ── */}
      <SEO
        title="About Hydrox Studios | Music & Video Production Studio in Guwahati, Assam"
        description="Hydrox Studios is a boutique music video and audio post-production studio in Guwahati, Assam. We serve artists and brands across Northeast India with precision, creativity, and cinematic quality."
        path="/about"
        canonicalUrl="https://hydroxstudios.com/about"
        ogImage="https://hydroxstudios.com/og-about.jpg"
        ogType="profile"
        keywords="about Hydrox Studios, music video studio Guwahati, audio production Assam, post-production Northeast India"
      />

      {/* ── JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ABOUT_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(BREADCRUMB_SCHEMA) }}
      />

      {/* ── Layout ── */}
      <main id="main-content">
        <PageHero />
        <StorySection />
        <PhilosophySection />
        <ApproachSection />
        <CTASection />
      </main>
    </div>
  );
}